/**
 * BrandSEO — runtime <head> branding per domain + per-page meta.
 *
 * Sets document.title, meta description, OG/Twitter tags, canonical URL,
 * favicon and theme-color from the active brand's `seo` block, overridden
 * by route-specific meta for key UCWonder pages.
 *
 * Known limitation: social scrapers that don't execute JS will read the
 * static index.html tags. Proper per-domain OG for link previews needs an
 * edge rewrite / prerender step at deploy time.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useBrand } from './useBrand';

/* Per-page meta for the UCWonder brand (key: 'marketplace') */
const UCWONDER_PAGE_META = {
  '/': {
    title: 'UCWonder — AI Trip Planner with Perks | Plan Your Whole Trip in Minutes',
    description: 'Chat with UCWonder AI and get a complete day-by-day itinerary — hotels, flights, activities and dining. Earn Wonders as you plan and unlock gift cards and perks from thousands of partner brands.',
  },
  '/ucwonder-ai': {
    title: 'UCWonder AI — Your Personal Travel Planner',
    description: 'Tell UCWonder AI where you dream of going. It builds your trip calendar, picks hotels, activities and restaurants, and hands you a ready-to-book plan in 4 simple steps. Free to try, no signup needed.',
  },
  '/wonders': {
    title: 'Wonders — Earn Points as You Plan Your Trip | UCWonder',
    description: "Wonders are UCWonder's reward points. Get 100 on signup, earn more with every trip you plan, and redeem for gift cards and lifestyle perks from thousands of partner brands. Never tied to bookings.",
  },
  '/experiences': {
    title: 'Experiences & Attractions — Find Things to Do | UCWonder',
    description: 'Browse curated experiences, attractions and activities for your next trip. From city tours to hidden gems, discover what to do and add it straight to your UCWonder AI itinerary.',
  },
  '/community': {
    title: 'UCWonder Community — Travel Stories & News',
    description: 'Trip inspiration, destination guides and product news from the UCWonder community. See what fellow travelers are planning and share your own journeys.',
  },
  '/dining': {
    title: 'Dining Perks — Restaurant Rewards for Travelers | UCWonder',
    description: 'Discover partner restaurants and dining perks for your trips. Plan your meals with UCWonder AI and enjoy exclusive partner benefits along the way.',
  },
  '/rewards-preview': {
    title: 'Perks Center — Gift Cards & Lifestyle Perks | UCWonder',
    description: 'Redeem your Wonders for gift cards and perks from thousands of global brands. Travel, dining, shopping and lifestyle rewards, all in one Perks Center.',
  },
  '/become-a-partner': {
    title: 'Become a Partner — Grow with UCWonder',
    description: 'Join the UCWonder partner ecosystem. Reach engaged travelers planning full trips with AI, and feature your brand in itineraries, perks and experiences.',
  },
  '/about': {
    title: 'About UCWonder — We Do the Mapping, You Do the Wondering',
    description: 'UCWonder pairs an AI trip planner with a rewards program travelers love. Learn about our mission, the team, and how we make trip planning effortless.',
  },
  '/privacy': { title: 'Privacy Policy | UCWonder', description: 'How UCWonder collects, uses and protects your data.' },
  '/terms': { title: 'Terms of Service | UCWonder', description: 'The terms that govern your use of UCWonder.' },
  '/data-security': { title: 'Data Security | UCWonder', description: 'How UCWonder keeps your account and travel data safe.' },
};

const setMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href) => {
  if (!href) return;
  let els = document.head.querySelectorAll(`link[rel="${rel}"]`);
  if (!els.length) {
    const el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
    els = [el];
  }
  els.forEach((el) => el.setAttribute('href', href));
};

export const BrandSEO = () => {
  const { brand } = useBrand();
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = brand?.seo;
    if (!seo) return;
    const cleanPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    const page = brand.key === 'marketplace' ? UCWONDER_PAGE_META[cleanPath] : null;
    const title = page?.title || seo.title;
    const description = page?.description || seo.description;

    document.title = title;
    setMeta('name', 'description', description);
    setMeta('name', 'theme-color', seo.themeColor);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:site_name', brand.displayName);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', `${seo.canonicalOrigin}${cleanPath}`);
    setMeta('property', 'og:image', seo.ogImage);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', seo.ogImage);
    setLink('canonical', `${seo.canonicalOrigin}${cleanPath}`);
    setLink('icon', seo.favicon);
    setLink('apple-touch-icon', seo.favicon);
  }, [brand, pathname]);

  return null;
};

export default BrandSEO;
